"use client"

import React, { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Box, Flex, Heading, Text, TextField, Button, Select, TextArea, Callout, Grid } from "@radix-ui/themes"
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Phone,
  Building2,
  MapPin,
  AlertCircle,
  CheckCircle,
  ChefHat,
  ArrowRight,
  Utensils
} from "lucide-react"
import { restaurantAuthAPI } from "../../services/restaurantApiClient"
import { useLanguage } from "../../contexts/LanguageContext"
import TurboLogo from "../../components/TurboLogo"
import LanguageSwitcher from "../../components/LanguageSwitcher"
import ReCAPTCHA from "react-google-recaptcha"
import { sudanStates, sudanCitiesByState } from "../../utils/sudanLocations"

interface RestaurantFormData {
  // Restaurant Information
  restaurantName: string
  description: string
  category: string
  street: string
  city: string
  state: string
  phone: string
  email: string
  minimumOrder: number

  // Owner Information
  ownerName: string
  ownerEmail: string
  ownerPassword: string
  confirmPassword: string
  ownerPhone: string
}

const RestaurantRegisterPage: React.FC = () => {
  const navigate = useNavigate()
  const { t, language, dir } = useLanguage()
  const [formData, setFormData] = useState<RestaurantFormData>({
    // Restaurant Information
    restaurantName: "",
    description: "",
    category: "",
    street: "",
    city: "",
    state: "",
    phone: "",
    email: "",
    minimumOrder: 0,

    // Owner Information
    ownerName: "",
    ownerEmail: "",
    ownerPassword: "",
    confirmPassword: "",
    ownerPhone: "",
  })

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [currentStep, setCurrentStep] = useState(1)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token)
  }

  const categories = [
    { key: "fastFood", value: "Fast food" },
    { key: "pizza", value: "Pizza" },
    { key: "asian", value: "Asian" },
    { key: "middleEastern", value: "Middle-eastern" },
    { key: "desserts", value: "Desserts" },
    { key: "healthy", value: "Healthy" },
    { key: "coffee", value: "Coffee" },
    { key: "burger", value: "Burger" },
    { key: "chicken", value: "Chicken" },
    { key: "breakfast", value: "Breakfast" }
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")
    setSuccess("")

    // Validate passwords match
    if (formData.ownerPassword !== formData.confirmPassword) {
      setError(t('passwordsDoNotMatch'))
      setLoading(false)
      return
    }

    // Validate password strength
    if (formData.ownerPassword.length < 6) {
      setError(t('passwordMinLength'))
      setLoading(false)
      return
    }

    if (!recaptchaToken) {
      setError(t('pleaseVerifyRecaptcha') || "Please verify you are not a robot")
      setLoading(false)
      return
    }

    try {
      // Create restaurant and owner in one API call
      await restaurantAuthAPI.registerRestaurant({
        restaurant: {
          name: formData.restaurantName,
          description: formData.description,
          category: formData.category,
          address: {
            street: formData.street,
            city: formData.city,
            state: formData.state,
            zipCode: "00000", // Default as field is removed from UI
            coordinates: { latitude: 0, longitude: 0 }
          },
          contact: {
            phone: formData.phone,
            email: formData.email
          },
          deliveryInfo: {
            fee: 0, // Default
            minimumOrder: formData.minimumOrder,
            estimatedTime: "30-45 min", // Default
            radius: 5 // Default
          }
        },
        owner: {
          name: formData.ownerName,
          email: formData.ownerEmail,
          password: formData.ownerPassword,
          phone: formData.ownerPhone
        },
        recaptchaToken
      })

      setSuccess(t('registrationSuccessful'))

      // Redirect to login page after a delay
      setTimeout(() => {
        navigate("/restaurant/login")
      }, 2000)
    } catch (error: any) {
      console.error("Registration error:", error)
      setError(error.userMessage || t('registrationFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.restaurantName || !formData.category || !formData.phone || !formData.email) {
        setError(t('fillAllFields'))
        return
      }
      setError("")
      setCurrentStep(2)
    }
  }

  const prevStep = () => {
    setError("")
    setCurrentStep(1)
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row" dir={dir}>
      {/* Left Side - Hero/Branding */}
      <div className="hidden md:flex md:w-1/2 bg-[#111] relative overflow-hidden flex-col justify-between p-12 text-white">
        <div className="absolute inset-0 z-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1500&q=80')] bg-cover bg-center" />
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-black/80 via-black/60 to-transparent" />

        <div className="relative z-10">
          <Link to="/" className="inline-block mb-8">
            <TurboLogo width={120} height={40} className="text-white" />
          </Link>
          <Heading size="8" className="font-bold mb-4 leading-tight">
            Grow your restaurant <br />
            <span className="text-red-500">with Turbo</span>
          </Heading>
          <Text size="5" className="text-gray-300 max-w-md">
            Join thousands of restaurants reaching new customers and increasing sales through our platform.
          </Text>
        </div>

        <div className="relative z-10 grid grid-cols-2 gap-8 mt-12">
          <div className="flex flex-col gap-2">
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center text-red-500 mb-2">
              <ChefHat size={24} />
            </div>
            <Text weight="bold" size="4">Restaurant Tools</Text>
            <Text size="2" className="text-gray-400">Advanced dashboard to manage orders, menus, and staff.</Text>
          </div>
          <div className="flex flex-col gap-2">
            <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-500 mb-2">
              <Utensils size={24} />
            </div>
            <Text weight="bold" size="4">More Orders</Text>
            <Text size="2" className="text-gray-400">Reach more customers in your area and boost your revenue.</Text>
          </div>
        </div>

        <div className="relative z-10 mt-auto pt-12">
          <Text size="2" className="text-gray-500">
            © {new Date().getFullYear()} Turbo Delivery. All rights reserved.
          </Text>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 bg-gray-50 dark:bg-gray-900 flex flex-col relative">
        <div className="absolute top-4 right-4 z-20">
          <LanguageSwitcher />
        </div>

        <div className="flex-1 flex flex-col justify-center p-6 md:p-12 max-w-2xl mx-auto w-full">
          {/* Mobile Header */}
          <div className="md:hidden mb-8 text-center">
            <TurboLogo width={80} height={30} className="mx-auto mb-4" />
            <Heading size="6">Partner with Turbo</Heading>
          </div>

          <div className="mb-8">
            <Flex align="center" gap="4" mb="6">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${currentStep === 1 ? 'bg-red-600 text-white' : 'bg-green-500 text-white'
                }`}>
                {currentStep > 1 ? <CheckCircle size={16} /> : '1'}
              </div>
              <div className="h-1 flex-1 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-red-600 transition-all duration-300 ease-out"
                  style={{ width: currentStep === 1 ? '50%' : '100%' }}
                />
              </div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${currentStep === 2 ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                2
              </div>
            </Flex>

            <Heading size="6" mb="2">
              {currentStep === 1 ? t('restaurantInformation') : t('ownerAccountInformation')}
            </Heading>
            <Text color="gray" size="2">
              {currentStep === 1
                ? "Tell us about your business to get started."
                : "Create your admin account to manage your restaurant."
              }
            </Text>
          </div>

          {error && (
            <Callout.Root color="red" className="mb-6">
              <Callout.Icon><AlertCircle /></Callout.Icon>
              <Callout.Text>{error}</Callout.Text>
            </Callout.Root>
          )}

          {success && (
            <Callout.Root color="green" className="mb-6">
              <Callout.Icon><CheckCircle /></Callout.Icon>
              <Callout.Text>{success}</Callout.Text>
            </Callout.Root>
          )}

          <form onSubmit={handleSubmit}>
            {currentStep === 1 && (
              <Flex direction="column" gap="5">
                <Grid columns={{ initial: '1', sm: '2' }} gap="5">
                  <Box style={{ gridColumn: 'span 2' }}>
                    <Text as="label" size="2" weight="medium" mb="1" className="block text-gray-700 dark:text-gray-300">
                      {t('restaurantName')} *
                    </Text>
                    <TextField.Root
                      placeholder="e.g. Tasty Burger"
                      size="3"
                      value={formData.restaurantName}
                      onChange={(e) => handleInputChange('restaurantName', e.target.value)}
                    >
                      <TextField.Slot><Building2 size={16} /></TextField.Slot>
                    </TextField.Root>
                  </Box>

                  <Box>
                    <Text as="label" size="2" weight="medium" mb="1" className="block text-gray-700 dark:text-gray-300">
                      {t('cuisineCategory')} *
                    </Text>
                    <Select.Root
                      value={formData.category}
                      onValueChange={(val) => handleInputChange('category', val)}
                      size="3"
                    >
                      <Select.Trigger placeholder={t('selectCategory')} className="w-full" />
                      <Select.Content>
                        {categories.map(cat => (
                          <Select.Item key={cat.key} value={cat.value} className="cursor-pointer">{t(cat.key)}</Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                  </Box>

                  <Box>
                    <Text as="label" size="2" weight="medium" mb="1" className="block text-gray-700 dark:text-gray-300">
                      {t('minimumOrder')} (SDG)
                    </Text>
                    <TextField.Root
                      type="number"
                      min="0"
                      placeholder="0"
                      size="3"
                      value={formData.minimumOrder || ''}
                      onChange={(e) => handleInputChange('minimumOrder', e.target.value)}
                    >
                      <TextField.Slot>$</TextField.Slot>
                    </TextField.Root>
                  </Box>

                  <Box style={{ gridColumn: 'span 2' }}>
                    <Text as="label" size="2" weight="medium" mb="1" className="block text-gray-700 dark:text-gray-300">
                      {t('description')}
                    </Text>
                    <TextArea
                      placeholder="Short description of your restaurant..."
                      size="3"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                    />
                  </Box>

                  <Box>
                    <Text as="label" size="2" weight="medium" mb="1" className="block text-gray-700 dark:text-gray-300">
                      {t('restaurantPhone')} *
                    </Text>
                    <TextField.Root
                      type="tel"
                      placeholder="+249..."
                      size="3"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                    >
                      <TextField.Slot><Phone size={16} /></TextField.Slot>
                    </TextField.Root>
                  </Box>

                  <Box>
                    <Text as="label" size="2" weight="medium" mb="1" className="block text-gray-700 dark:text-gray-300">
                      {t('restaurantEmail')} *
                    </Text>
                    <TextField.Root
                      type="email"
                      placeholder="info@restaurant.com"
                      size="3"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                    >
                      <TextField.Slot><Mail size={16} /></TextField.Slot>
                    </TextField.Root>
                  </Box>
                </Grid>

                <div className="my-2 border-t border-gray-200 dark:border-gray-700" />

                <Heading size="3" className="text-gray-700 dark:text-gray-300">{t('location')}</Heading>

                <Grid columns={{ initial: '1', sm: '2' }} gap="5">
                  <Box>
                    <Text as="label" size="2" weight="medium" mb="1" className="block text-gray-700 dark:text-gray-300">
                      {t('state')} *
                    </Text>
                    <Select.Root
                      value={formData.state}
                      onValueChange={(val) => {
                        setFormData(prev => ({ ...prev, state: val, city: "" }))
                      }}
                      size="3"
                    >
                      <Select.Trigger placeholder="Select State" className="w-full" />
                      <Select.Content>
                        {sudanStates.map(state => (
                          <Select.Item key={state.value} value={state.value} className="cursor-pointer">
                            {language === 'ar' ? state.labelAr : state.labelEn}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                  </Box>

                  <Box>
                    <Text as="label" size="2" weight="medium" mb="1" className="block text-gray-700 dark:text-gray-300">
                      {t('city')} *
                    </Text>
                    <Select.Root
                      value={formData.city}
                      onValueChange={(val) => handleInputChange('city', val)}
                      size="3"
                      disabled={!formData.state}
                    >
                      <Select.Trigger placeholder="Select City" className="w-full" />
                      <Select.Content>
                        {(sudanCitiesByState[formData.state] || []).map(city => (
                          <Select.Item key={city.value} value={city.value} className="cursor-pointer">
                            {language === 'ar' ? city.labelAr : city.labelEn}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                  </Box>

                  <Box style={{ gridColumn: 'span 2' }}>
                    <Text as="label" size="2" weight="medium" mb="1" className="block text-gray-700 dark:text-gray-300">
                      {t('streetAddress')} *
                    </Text>
                    <TextField.Root
                      placeholder="Street, Building, Landmark..."
                      size="3"
                      value={formData.street}
                      onChange={(e) => handleInputChange('street', e.target.value)}
                    >
                      <TextField.Slot><MapPin size={16} /></TextField.Slot>
                    </TextField.Root>
                  </Box>
                </Grid>

                <Button
                  size="3"
                  type="button"
                  onClick={nextStep}
                  className="w-full mt-4 bg-red-600 hover:bg-red-700 text-white cursor-pointer"
                >
                  {t('nextOwnerInformation')} <ArrowRight size={16} />
                </Button>
              </Flex>
            )}

            {currentStep === 2 && (
              <Flex direction="column" gap="5">
                <Box>
                  <Text as="label" size="2" weight="medium" mb="1" className="block text-gray-700 dark:text-gray-300">
                    {t('fullName')} *
                  </Text>
                  <TextField.Root
                    placeholder="Your full name"
                    size="3"
                    value={formData.ownerName}
                    onChange={(e) => handleInputChange('ownerName', e.target.value)}
                  >
                    <TextField.Slot><User size={16} /></TextField.Slot>
                  </TextField.Root>
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium" mb="1" className="block text-gray-700 dark:text-gray-300">
                    {t('emailAddress')} *
                  </Text>
                  <TextField.Root
                    type="email"
                    placeholder="owner@example.com"
                    size="3"
                    value={formData.ownerEmail}
                    onChange={(e) => handleInputChange('ownerEmail', e.target.value)}
                  >
                    <TextField.Slot><Mail size={16} /></TextField.Slot>
                  </TextField.Root>
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium" mb="1" className="block text-gray-700 dark:text-gray-300">
                    {t('phoneNumber')} *
                  </Text>
                  <TextField.Root
                    type="tel"
                    placeholder="+249..."
                    size="3"
                    value={formData.ownerPhone}
                    onChange={(e) => handleInputChange('ownerPhone', e.target.value)}
                  >
                    <TextField.Slot><Phone size={16} /></TextField.Slot>
                  </TextField.Root>
                </Box>

                <Grid columns={{ initial: '1', sm: '2' }} gap="5">
                  <Box>
                    <Text as="label" size="2" weight="medium" mb="1" className="block text-gray-700 dark:text-gray-300">
                      {t('password')} *
                    </Text>
                    <TextField.Root
                      type={showPassword ? "text" : "password"}
                      placeholder="Min 6 characters"
                      size="3"
                      value={formData.ownerPassword}
                      onChange={(e) => handleInputChange('ownerPassword', e.target.value)}
                    >
                      <TextField.Slot><Lock size={16} /></TextField.Slot>
                      <TextField.Slot>
                        <button type="button" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </TextField.Slot>
                    </TextField.Root>
                  </Box>

                  <Box>
                    <Text as="label" size="2" weight="medium" mb="1" className="block text-gray-700 dark:text-gray-300">
                      {t('confirmPassword')} *
                    </Text>
                    <TextField.Root
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-enter password"
                      size="3"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    >
                      <TextField.Slot><Lock size={16} /></TextField.Slot>
                      <TextField.Slot>
                        <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                          {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </TextField.Slot>
                    </TextField.Root>
                  </Box>
                </Grid>

                <Flex justify="center" mt="4">
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                    onChange={handleRecaptchaChange}
                  />
                </Flex>

                <Flex gap="3" mt="2">
                  <Button
                    variant="soft"
                    color="gray"
                    size="3"
                    type="button"
                    onClick={prevStep}
                    className="flex-1 cursor-pointer"
                  >
                    {t('back')}
                  </Button>
                  <Button
                    size="3"
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white cursor-pointer"
                  >
                    {loading ? 'Creating Account...' : t('createRestaurantAccount')}
                  </Button>
                </Flex>
              </Flex>
            )}
          </form>

          <div className="mt-8 text-center">
            <Text size="2" color="gray">
              {t('alreadyHaveAccount')}{" "}
              <Link to="/restaurant/login" className="text-red-600 hover:underline font-medium">
                {t('signInHere')}
              </Link>
            </Text>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RestaurantRegisterPage